import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../utils/api';
import { Loading } from '../../components/Shared';

const STATUTS = ['a_envoyer', 'envoye', 'converti', 'ignore'];
const STATUT_LABELS = {
  a_envoyer: '🔔 À envoyer',
  envoye:    '📨 Envoyé',
  converti:  '✅ Converti',
  ignore:    '🚫 Ignoré',
};
const STATUT_COLORS = {
  a_envoyer: { bg: 'rgba(245,158,11,0.15)',  color: '#f59e0b' },
  envoye:    { bg: 'rgba(74,158,255,0.12)',  color: '#4a9eff' },
  converti:  { bg: 'rgba(52,211,153,0.15)',  color: '#34d399' },
  ignore:    { bg: 'rgba(92,96,120,0.2)',    color: '#b8bdd0' },
};

const fmtDate = d => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

function isOverdue(d) {
  return d && new Date(d) < new Date();
}

export default function CRMRappels() {
  const [items, setItems]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [filterStatut, setFilterStatut] = useState('a_envoyer');
  const [generating, setGenerating] = useState(false);

  const LIMIT = 30;

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: LIMIT });
    if (filterStatut) params.set('statut', filterStatut);
    apiFetch(`/crm/rappels-recyclage?${params}`)
      .then(r => { setItems(r.data || []); setTotal(r.total || 0); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [page, filterStatut]);

  useEffect(() => { load(); }, [load]);

  async function handleGenerer() {
    if (!window.confirm('Générer les rappels de recyclage pour les prochains 30 jours ?')) return;
    setGenerating(true);
    try {
      const res = await apiFetch('/crm/rappels-recyclage/generer', { method: 'POST' });
      alert(`${res.created || 0} rappel(s) créé(s).`);
      load();
    } catch (e) { alert('Erreur : ' + e.message); }
    finally { setGenerating(false); }
  }

  async function handleStatut(id, statut) {
    try {
      await apiFetch(`/crm/rappels-recyclage/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ statut }),
      });
      load();
    } catch (e) { alert('Erreur : ' + e.message); }
  }

  return (
    <div className="crm-section-page">
      <div className="crm-toolbar">
        <div className="crm-filters">
          <select className="crm-select" value={filterStatut}
            onChange={e => { setFilterStatut(e.target.value); setPage(1); }}>
            <option value="">Tous les statuts</option>
            {STATUTS.map(s => <option key={s} value={s}>{STATUT_LABELS[s]}</option>)}
          </select>
        </div>
        <button className="btn btn-primary crm-btn-sm" onClick={handleGenerer} disabled={generating}>
          {generating ? 'Génération…' : '♻️ Générer rappels'}
        </button>
      </div>

      <div className="crm-rappels-info">
        <span>Les rappels de recyclage sont générés automatiquement à partir des habilitations
        arrivant à échéance. Cliquez sur « Générer rappels » pour créer les entrées
        correspondant aux 30 prochains jours.</span>
      </div>

      {loading && <Loading />}
      {error && <div className="crm-error">{error}</div>}

      {!loading && !error && (
        <>
          <div className="crm-table-wrap">
            <table className="crm-table">
              <thead>
                <tr>
                  <th>Stagiaire</th>
                  <th>Client</th>
                  <th>Formation</th>
                  <th>Échéance recyclage</th>
                  <th>Date rappel</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr><td colSpan={7} className="crm-empty-row">Aucun rappel trouvé</td></tr>
                )}
                {items.map(r => {
                  const sc = STATUT_COLORS[r.statut] || STATUT_COLORS.a_envoyer;
                  const overdue = isOverdue(r.date_rappel_prevu);
                  return (
                    <tr key={r.id} className="crm-table-row">
                      <td><strong>{r.stagiaire_nom || '—'}</strong></td>
                      <td className="crm-text-sm">{r.client_nom || '—'}</td>
                      <td className="crm-text-sm">{r.formation_nom || '—'}</td>
                      <td style={{ color: isOverdue(r.date_echeance) ? '#f87171' : 'inherit' }}>
                        {fmtDate(r.date_echeance)}
                        {isOverdue(r.date_echeance) && <span> ⚠</span>}
                      </td>
                      <td style={{ color: overdue && r.statut === 'a_envoyer' ? '#f87171' : 'inherit' }}>
                        {fmtDate(r.date_rappel_prevu)}
                        {overdue && r.statut === 'a_envoyer' && <span> ⚠</span>}
                      </td>
                      <td>
                        <span className="crm-badge" style={sc}>{STATUT_LABELS[r.statut]}</span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                          {r.statut === 'a_envoyer' && (
                            <>
                              <button className="crm-card-btn"
                                onClick={() => handleStatut(r.id, 'envoye')}
                                title="Marquer comme envoyé">
                                📨
                              </button>
                              <button className="crm-card-btn"
                                onClick={() => handleStatut(r.id, 'ignore')}
                                title="Ignorer">
                                🚫
                              </button>
                            </>
                          )}
                          {r.statut === 'envoye' && (
                            <button className="crm-card-btn"
                              onClick={() => handleStatut(r.id, 'converti')}
                              title="Marquer comme converti (réinscription)">
                              ✅
                            </button>
                          )}
                          {(r.statut === 'envoye' || r.statut === 'converti' || r.statut === 'ignore') && (
                            <button className="crm-card-btn"
                              onClick={() => handleStatut(r.id, 'a_envoyer')}
                              title="Remettre à envoyer">
                              ↩
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {total > LIMIT && (
            <div className="crm-pagination">
              <button disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
              <span>Page {page} / {Math.ceil(total / LIMIT)}</span>
              <button disabled={page >= Math.ceil(total / LIMIT)} onClick={() => setPage(p => p + 1)}>›</button>
            </div>
          )}
          <div className="crm-count">{total} rappel{total > 1 ? 's' : ''}</div>
        </>
      )}
    </div>
  );
}
